import {
  BaseQueryApi,
  BaseQueryFn
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/dist/query/react';
import { Mutex } from 'async-mutex';

import { API_URL, END_POINTS } from '@config';
import ApiTags from '@constants/api-tags';
import {
  ErrorCode,
  OrgLevel403ErrorCodes,
  OrgLevel500ErrorCodes,
  RoleLevel403ErrorCodes
} from '@constants/errorCodes';
import { UserTypes } from '@constants/role';
import {
  AUTH,
  DASHBOARD,
  LEAD_MANAGEMENT,
  PAGE_NOT_FOUND
} from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  fetchRealOrgConfigVisibilityFlags,
  isOrgConfigRequest,
  isTrialPassthrough,
  mergeRealOrgConfigVisibilityFlags,
  resolveMockData
} from '@features/trial-mode/mock-data';
import { isOrgRestricted } from '@helpers/orgRestriction';
import { hasAnyRoleScope, LEAD_MANAGEMENT_ROLES } from '@helpers/roleScope';
import {
  setNoBranchAccess,
  setRestrictionModal
} from '@layouts/main-header/userSlice';
import { ApiError, GetOrganizationListResponse, TokenResponse } from '@types';
import {
  clearActiveBranchFromStorage,
  clearAllLocalStorageItems,
  getLocalStorageItem,
  setLocalStorageItem,
  setTokensInStorage
} from '@utils/storage';
import { getUrlStruct } from '@utils/urlStruct';
import { Org } from 'types/organization';

import { Event, sendMixpanelEvent } from './analytics/mixpanel';

const mutex = new Mutex();

const SLOW_API_THRESHOLD_MS = 4000;

const redirectToLogin = () => {
  clearAllLocalStorageItems();
  if (window.location.pathname === AUTH.LOGIN) return;
  window.location.replace(AUTH.LOGIN);
};

// Endpoints that must keep working even after the active organisation is blacklisted.
// A churned org is gated the same way, so the allowed set covers both reasons.
// `getDynamicComponents` is allowed because the churn popup itself is served by it.
const RESTRICTED_ORG_ALLOWED_ENDPOINTS = new Set([
  'getOrganizationList',
  'logout',
  'getDynamicComponents'
]);

// To check whether the active organization is currently blacklisted.
// Churn counts as restricted too — see `isOrgRestricted`.
const isActiveOrgRestricted = (api: BaseQueryApi): boolean => {
  const state = api.getState() as {
    user?: { restrictionModal?: { orgList?: Org[] } | null };
  };
  const orgList = state.user?.restrictionModal?.orgList;
  if (!orgList?.length) return false;
  const currentOrgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);
  return orgList.some((org) => org.id === currentOrgId && isOrgRestricted(org));
};

// Extract the organisation id a request targets from its URL.
const getRequestOrgId = (args: string | FetchArgs): string => {
  const url = typeof args === 'string' ? args : args?.url;
  return url?.match(/organisation\/([^/?]+)/)?.[1] || '';
};

// Extract the branch id a request targets from its URL.
const getRequestBranchId = (args: string | FetchArgs): string => {
  const url = typeof args === 'string' ? args : args?.url;
  return url?.match(/branch\/([^/?]+)/)?.[1] || '';
};

// Concurrent requests all 403 once the active branch is gone; resolve it once.
let isResolvingLostBranch = false;
let isRedirectingToNewBranch = false;

const getPlatformFromUserAgent = (userAgent: string): string => {
  if (/android/i.test(userAgent)) return 'Web - Android';
  if (/ipad|iphone|ipod/i.test(userAgent)) return 'Web - Ios';
  return 'Web';
};

const parseUserAgent = (ua: string) => {
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
    ? 'Mobile'
    : 'Desktop';

  const osMatch =
    ua.match(
      // eslint-disable-next-line no-useless-escape
      /(Android|Windows NT|iPhone OS|iPad; CPU OS|Mac OS X|Linux)[\s\/]?([\d\._]*)/
    ) || [];
  const os =
    osMatch[1]?.replace('Windows NT', 'Windows')?.replace('iPhone OS', 'iOS') ||
    'Unknown OS';
  const osVersion = osMatch[2]?.replace(/_/g, '.') || '';

  const browserMatch =
    // eslint-disable-next-line no-useless-escape
    ua.match(/(Chrome|Safari|Firefox|Edge|SamsungBrowser)\/([\d\.]+)/) || [];
  const browser = browserMatch[1] || 'Unknown Browser';
  const browserVersion = browserMatch[2] || '';

  const appMatch =
    ua.match(/Instagram|FBAV|FBAN|WhatsApp|Snapchat|Line|WeChat/) || [];
  const app = appMatch[0] || ''; // Empty if regular browser

  return `${isMobile}; ${os} ${osVersion}; ${browser} ${browserVersion}${
    app ? `; ${app}` : ''
  }`;
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${API_URL}`,
  prepareHeaders: (headers) => {
    const token = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const organizationId = getLocalStorageItem(
      LOCAL_STORAGE_KEYS.ORGANIZATION_ID
    );
    if (organizationId) {
      headers.set('x-org-id', organizationId);
    }

    const platform = window.navigator.userAgent;
    const deviceType = getPlatformFromUserAgent(platform);
    const parsedUserAgent = parseUserAgent(platform);
    headers.set('X-Device-Platform', parsedUserAgent);
    headers.set('X-Device-Type', deviceType);

    return headers;
  }
});

const fetchOrgList = async (
  api: BaseQueryApi,
  extraOptions: {}
): Promise<GetOrganizationListResponse> => {
  const orgListResult = await baseQuery(
    {
      url: END_POINTS.ORGANIZATION.GET_ORGANIZATION_LIST,
      method: 'GET'
    },
    api,
    extraOptions
  );
  return (orgListResult?.data as GetOrganizationListResponse) || [];
};

const setActiveBranchToDefault = (org?: Org) => {
  if (!org) return;
  setLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID, org.default_branch_id);
  setLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_NAME, org.default_branch_name);
};

// Re-read the org list and, when the active org turns out to be restricted
// (blacklisted or churned), raise the restriction modal and keep the session.
const keepSessionForRestrictedOrg = async (
  api: BaseQueryApi,
  extraOptions: {},
  currentOrgId: string
): Promise<boolean> => {
  const orgList = await fetchOrgList(api, extraOptions);
  const currentOrg = orgList.find((org: Org) => org.id === currentOrgId);

  if (!isOrgRestricted(currentOrg)) return false;

  api.dispatch(
    setRestrictionModal({
      orgList
    })
  );
  return true;
};

// The active branch was archived (or the user lost access to it). Reload into
// the next branch the backend offers, or raise the no-branch modal when none
// is left. Returns false when the branch is still accessible.
const recoverFromLostActiveBranch = async (
  api: BaseQueryApi,
  extraOptions: {},
  currentOrgId: string
): Promise<boolean> => {
  const orgList = await fetchOrgList(api, extraOptions);
  const org = orgList.find((item: Org) => item.id === currentOrgId);
  if (!org) return false;

  if (!org.default_branch_id) {
    // Drop the lost branch so a refresh doesn't show its name again.
    clearActiveBranchFromStorage();
    api.dispatch(setNoBranchAccess(true));
    return true;
  }
  if (
    org.default_branch_id === getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID)
  ) {
    return false;
  }

  setActiveBranchToDefault(org);
  isRedirectingToNewBranch = true;
  // Reload from home so no screen keeps ids from the lost branch.
  window.location.replace(
    hasAnyRoleScope(org, LEAD_MANAGEMENT_ROLES)
      ? LEAD_MANAGEMENT.LEAD_DASHBOARD
      : DASHBOARD
  );
  return true;
};

const handleBranchForbidden = async (
  api: BaseQueryApi,
  extraOptions: {},
  currentOrgId: string
): Promise<boolean> => {
  const state = api.getState() as { user?: { noBranchAccess?: boolean } };
  if (
    isResolvingLostBranch ||
    isRedirectingToNewBranch ||
    state.user?.noBranchAccess
  ) {
    return true;
  }

  isResolvingLostBranch = true;
  try {
    return await recoverFromLostActiveBranch(api, extraOptions, currentOrgId);
  } finally {
    isResolvingLostBranch = false;
  }
};

const handle403Error = async (
  errorCode: string,
  api: BaseQueryApi,
  extraOptions: {},
  requestOrgId: string,
  requestBranchId: string
) => {
  const currentOrgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);

  // Ignore 403s from stale requests that target a different org than the active one
  if (requestOrgId && requestOrgId !== currentOrgId) return;

  const code = errorCode as ErrorCode;
  const isActiveBranchForbidden =
    code === ErrorCode.ERR_BRANCH_FORBIDDEN &&
    requestBranchId === getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID);

  if (isActiveBranchForbidden) {
    const isHandled = await handleBranchForbidden(
      api,
      extraOptions,
      currentOrgId
    );
    if (isHandled) return;
  }

  // An org-level request (e.g. branch revenue) filtered by a branch that was
  // archived or revoked meanwhile. Only the filter is stale, not the page, so
  // leave it to the caller to refresh its branch options.
  const isBranchFilterForbidden =
    code === ErrorCode.ERR_BRANCH_FORBIDDEN && !requestBranchId;
  if (isBranchFilterForbidden) return;

  if (RoleLevel403ErrorCodes.includes(code)) {
    const orgList = await fetchOrgList(api, extraOptions);
    setActiveBranchToDefault(
      orgList.find((org: Org) => org.id === currentOrgId)
    );
    window.location.replace(PAGE_NOT_FOUND);
  } else if (OrgLevel403ErrorCodes.includes(code)) {
    // The current org is forbidden. If it is specifically blacklisted, keep the
    // session and let the UI show the blacklisted modal instead of logging out.
    // A churned org is restricted the same way, so it keeps its session too.
    const isRestricted = await keepSessionForRestrictedOrg(
      api,
      extraOptions,
      currentOrgId
    );
    if (isRestricted) return;

    redirectToLogin();
  }
};

const handle500Error = async (
  errorCode: string,
  userType: string,
  requestOrgId: string,
  api: BaseQueryApi,
  extraOptions: {}
) => {
  // Ignore 500s from stale requests targeting a different org than the active
  // one so they don't force a logout after switching accounts.
  const currentOrgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);
  if (requestOrgId && requestOrgId !== currentOrgId) return;

  if (
    userType !== UserTypes.ADMIN &&
    OrgLevel500ErrorCodes.includes(errorCode as ErrorCode)
  ) {
    const isRestricted = await keepSessionForRestrictedOrg(
      api,
      extraOptions,
      currentOrgId
    );
    if (isRestricted) return;

    clearAllLocalStorageItems();
    redirectToLogin();
  }
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as {
    trial?: {
      isTrialMode: boolean;
      originalAccountType?: string | null;
      originalStorageValues?: Record<string, string> | null;
    };
  };
  const realOrgId =
    state.trial?.originalStorageValues?.[LOCAL_STORAGE_KEYS.ORGANIZATION_ID];
  if (state.trial?.isTrialMode && !isTrialPassthrough(args, realOrgId)) {
    const mockResponse = resolveMockData(
      args,
      state.trial?.originalAccountType || undefined
    );
    if (mockResponse !== undefined) {
      if (realOrgId && isOrgConfigRequest(args)) {
        const realFlags = await fetchRealOrgConfigVisibilityFlags(realOrgId);
        return {
          data: mergeRealOrgConfigVisibilityFlags(mockResponse, realFlags)
        };
      }
      return { data: mockResponse };
    }
  }

  // Block all requests once the active org is blacklisted, except allowed endpoints.
  // Churned orgs are blocked by the same kill-switch.
  if (
    !RESTRICTED_ORG_ALLOWED_ENDPOINTS.has(api.endpoint) &&
    isActiveOrgRestricted(api)
  ) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Request blocked: active organisation is restricted'
      }
    };
  }

  await mutex.waitForUnlock();

  // The org this request actually targets. Prefer the org id embedded in the
  // request URL over the stored one, so a stale request fired against a
  // previous org
  const requestOrgId =
    getRequestOrgId(args) ||
    getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);
  const requestSentAt = new Date();
  const startTime = performance.now();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = getLocalStorageItem(
          LOCAL_STORAGE_KEYS.REFRESH_TOKEN
        );
        const mobile = getLocalStorageItem(LOCAL_STORAGE_KEYS.MOBILE);
        const refreshResult = await baseQuery(
          {
            url: END_POINTS.AUTH.REFRESH,
            method: 'POST',
            body: { refresh_token: refreshToken, mobile: String(mobile) }
          },
          api,
          extraOptions
        );
        if (refreshResult.data) {
          setTokensInStorage(refreshResult.data as TokenResponse);
          // Retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          redirectToLogin();
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  } else if (result.error) {
    const error = result.error as ApiError;
    const errorCode = error.data?.errorCode;
    const isStaleOrgRequest =
      !!requestOrgId &&
      requestOrgId !== getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);

    if (
      errorCode === ErrorCode.ERR_ORGANISATION_NOT_FOUND &&
      !isStaleOrgRequest
    ) {
      redirectToLogin();
    }

    if (error.status === 403 && errorCode) {
      await handle403Error(
        errorCode,
        api,
        extraOptions,
        requestOrgId,
        getRequestBranchId(args)
      );
    } else if (error.status === 500 && errorCode) {
      const userType = getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_TYPE);
      handle500Error(errorCode, userType, requestOrgId, api, extraOptions);
    }
  }

  const networkDuration = performance.now() - startTime;
  if (networkDuration > SLOW_API_THRESHOLD_MS) {
    const url = typeof args === 'string' ? args : args?.url;
    const method = typeof args === 'string' ? 'GET' : args?.method || 'GET';
    const userState = api.getState() as {
      user?: {
        currentOrg?: { name?: string; role_name?: string };
        currentUser?: { is_internal?: boolean };
        rolesAndPermissions?: { role_name?: string };
      };
    };

    sendMixpanelEvent(Event.SLOW_API_RESPONSE, {
      network_duration_ms: Math.round(networkDuration),
      url,
      method: method.toLowerCase(),
      endpoint: getUrlStruct(url),
      org_name: userState.user?.currentOrg?.name || '',
      module: 'web_app',
      request_sent_at: requestSentAt.toISOString(),
      response_received_at: new Date().toISOString(),
      user_role:
        userState.user?.rolesAndPermissions?.role_name ||
        userState.user?.currentOrg?.role_name ||
        '',
      internal: Boolean(userState.user?.currentUser?.is_internal)
    });
  }

  return result;
};

// eslint-disable-next-line import/prefer-default-export
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  refetchOnMountOrArgChange: false,
  refetchOnReconnect: false,
  tagTypes: Object.values(ApiTags)
});
